import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { fetchScreenshots, createLabel, fetchLabelsForRun, deleteLabel, downloadModelOutputFile, downloadStartState, createScoreDispute, ScoreDisputeCreate, fetchProblemRunDisputes, invalidateAPICache, API_BASE_URL, getAuthHeaders, acknowledgeMistake, deleteDispute, deleteMistakeAcknowledgment, getUserAcknowledgments } from '../api';
import { Screenshot, ScreenshotData, LabelType, ScoreDispute, HumanLabel } from '../types';
import ScreenshotDisplay from './ScreenshotDisplay';
import NavigationControls from './NavigationControls';
import KeyboardShortcuts from './KeyboardShortcuts';
import OnboardingOverlayNew from './OnboardingOverlayNew';
import { IndicatorType } from './NavigationIndicator';
import ContextMenu from './ContextMenu';
import DisputeModal from './DisputeModal';
import DownloadToast from './DownloadToast';
import { isOverdue } from '../utils/dateFormatting';
import { extractPromptFromMessages } from '../utils/promptExtractor';
import { useTutorial, getTutorialConfig, TutorialStep } from '../services/tutorial';
import { preloadService } from '../services/PreloadService';
import './ScreenshotViewer.css';
import VideoComparisonModal from './VideoComparisonModal';
import PlaygroundModal from './PlaygroundModal';

const BAD_TASK_PREFIX = 'BAD_TASK';

const parseLabelNotes = (notes?: string | null): { reason: string; isBadTask: boolean } => {
  if (!notes) {
    return { reason: '', isBadTask: false };
  }

  const trimmed = notes.trim();
  if (trimmed === BAD_TASK_PREFIX) {
    return { reason: '', isBadTask: true };
  }

  if (trimmed.startsWith(`${BAD_TASK_PREFIX}:`)) {
    return {
      reason: trimmed.slice(`${BAD_TASK_PREFIX}:`.length).trim(),
      isBadTask: true,
    };
  }

  return { reason: trimmed, isBadTask: false };
};

const isBadTaskNotes = (notes?: string | null): boolean => parseLabelNotes(notes).isBadTask;

interface ScreenshotViewerProps {
  problemName: string;
  runId: string;
  problemInfo: string;
  problemPrompt: string;
  progressInfo: { current: number; total: number };
  onNextProblem?: () => void;
  onPrevProblem?: () => void;
  hasNextProblem: boolean;
  hasPrevProblem: boolean;
  isOnboarding?: boolean;
  onOnboardingComplete?: () => void;
  onNavigateToFirst?: () => void;
  onBackToProjects?: () => void;
  currentOnboardingTask?: number;
  onboardingMessages?: any[];
  software?: string;
  problemId?: string;
  projectId?: string;
  projectDueDate?: string | null;
  viewingMistake?: boolean;
  mistakeInfo?: {
    userLabel: string;
    actualResult: string;
    labeledAt: string;
    humanLabelId: string;
    userLabelNotes?: string | null;
    allLabels?: Array<{
      user_id: string;
      username: string;
      label: string;
      notes?: string | null;
    }>;
  };
  disputeInfo?: {
    status: string;
    proposedScore: string;
    note: string;
    reason: string | null;
    createdAt: string;
    current_score?: number | null;
    final_score?: number | null;
  };
  isDisputeMode?: boolean;
  allDisputes?: ScoreDispute[];
  onOpenDisputeModal?: (dispute: ScoreDispute) => void;
  needsPromptLoad?: boolean;
  runStatus?: "pending" | "running" | "completed" | "failed" | "cancelled";
  userPermissions?: string[];
  onScreenshotsLoaded?: () => void;
  onRunLabelChange?: (runId: string, label: HumanLabel | null) => void;
  isReadOnly?: boolean;
  tutorialRunLoading?: boolean;
  mattermostState?: any;
}

const ScreenshotViewer: React.FC<ScreenshotViewerProps> = ({
  problemName,
  runId,
  problemInfo,
  problemPrompt,
  progressInfo,
  onNextProblem,
  onPrevProblem,
  hasNextProblem,
  hasPrevProblem,
  isOnboarding = false,
  onOnboardingComplete,
  onNavigateToFirst,
  onBackToProjects,
  currentOnboardingTask = 0,
  onboardingMessages = [],
  software,
  problemId,
  projectId,
  projectDueDate = null,
  viewingMistake = false,
  mistakeInfo,
  disputeInfo,
  allDisputes = [],
  isDisputeMode = false,
  onOpenDisputeModal,
  needsPromptLoad = false,
  runStatus,
  userPermissions = [],
  onScreenshotsLoaded,
  onRunLabelChange,
  isReadOnly = false,
  tutorialRunLoading = false,
  mattermostState,
}) => {
  // Parse project ID from URL as the source of truth
  const getProjectIdFromUrl = (): string | undefined => {
    const path = window.location.pathname;
    const projectMatch = path.match(/^\/project\/([^\/]+)/);
    return projectMatch ? projectMatch[1] : undefined;
  };

  // Use URL-based project ID as primary source, fall back to prop
  // Re-calculate on every render to ensure we catch URL changes
  const [urlProjectId, setUrlProjectId] = React.useState(getProjectIdFromUrl());
  
  // Monitor URL changes
  React.useEffect(() => {
    const checkUrl = () => {
      const newProjectId = getProjectIdFromUrl();
      if (newProjectId !== urlProjectId) {
        console.log('URL project ID changed:', urlProjectId, '->', newProjectId);
        setUrlProjectId(newProjectId);
      }
    };
    
    // Check immediately
    checkUrl();
    
    // Check on popstate events
    window.addEventListener('popstate', checkUrl);
    
    // Check periodically in case pushState doesn't trigger popstate
    const interval = setInterval(checkUrl, 100);
    
    return () => {
      window.removeEventListener('popstate', checkUrl);
      clearInterval(interval);
    };
  }, [urlProjectId]);
  
  const effectiveProjectId = urlProjectId || projectId;
  
  // Log when there's no project ID in labeling context
  React.useEffect(() => {
    if (!viewingMistake && !isOnboarding && !effectiveProjectId) {
      console.error('No effective project ID in labeling context!', {
        url: window.location.pathname,
        projectIdProp: projectId,
        urlProjectId: getProjectIdFromUrl()
      });
    }
  }, [effectiveProjectId, viewingMistake, isOnboarding, projectId]);
  
  // Validate that we're in a proper labeling context
  const isInProjectLabelingContext = () => {
    const path = window.location.pathname;
    return /\/project\/[^\/]+(?:\/problems\/[^\/]+)?\/runs\/\d+/.test(path);
  };
  
  const [screenshotData, setScreenshotData] = useState<ScreenshotData | null>(null);
  const [loadedPrompt, setLoadedPrompt] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 2x, 4x speed
  const [evaluation, setEvaluation] = useState<LabelType | null>(null);
  const [submittingLabel, setSubmittingLabel] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isBadTask, setIsBadTask] = useState(false);
  const [navigationIndicator, setNavigationIndicator] = useState<IndicatorType>(null);
  const [indicatorKey, setIndicatorKey] = useState(0);
  const tutorial = useTutorial();
  const tutorialConfig = getTutorialConfig(software || tutorial.state.software);
  const [onboardingStep, setOnboardingStep] = useState(isOnboarding ? tutorial.state.currentStepId || 'welcome' : '');
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showWrongEvaluationDialog, setShowWrongEvaluationDialog] = useState(false);
  const [showCorrectEvaluationDialog, setShowCorrectEvaluationDialog] = useState(false);
  const [currentLabelId, setCurrentLabelId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [showVideoComparisonModal, setShowVideoComparisonModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [downloadFilename, setDownloadFilename] = useState<string>('');
  const downloadToastTimeoutRef = useRef<number | null>(null);
  const [showFirstTimeTip, setShowFirstTimeTip] = useState(false);
  const [showVideoComparisonTip, setShowVideoComparisonTip] = useState(false);

  const handleCloseDownloadToast = useCallback(() => {
    if (downloadToastTimeoutRef.current !== null) {
      window.clearTimeout(downloadToastTimeoutRef.current);
      downloadToastTimeoutRef.current = null;
    }
    setDownloadProgress(null);
    setDownloadFilename('');
  }, []);
  const [hasExistingDispute, setHasExistingDispute] = useState(false);
  const [userDisputeId, setUserDisputeId] = useState<string | null>(null);
  const [hasAcknowledgedMistake, setHasAcknowledgedMistake] = useState(false);
  const [acknowledgingMistake, setAcknowledgingMistake] = useState(false);
  const [userLabelDeleted, setUserLabelDeleted] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<'dispute' | 'acknowledgment' | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [acknowledgmentId, setAcknowledgmentId] = useState<string | null>(null);
  const [problemRunNotCompleted, setProblemRunNotCompleted] = useState(false);
  const [mistakeLabelNotes, setMistakeLabelNotes] = useState<string | null>(null);
  const [labelReason, setLabelReason] = useState<string>('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [isPlaygroundModalOpen, setIsPlaygroundModalOpen] = useState(false);
  const [pendingEvaluation, setPendingEvaluation] = useState<{ type: 'success' | 'fail' | 'bad-task'; labelType: LabelType } | null>(null);
  const [modalReasonInput, setModalReasonInput] = useState<string>('');
  const mistakeIsBadTask = isBadTaskNotes(mistakeLabelNotes);
  const [completedEvaluationSteps, setCompletedEvaluationSteps] = useState<Set<string>>(new Set());


  // Check if project is overdue
  const isProjectOverdue = !!(projectDueDate && isOverdue(projectDueDate));
  const currentTutorialStep = tutorialConfig?.steps.find(
    (step) => step.id === tutorial.state.currentStepId,
  );
  const showViewerTutorialOverlay =
    isOnboarding &&
    tutorial.state.isActive &&
    onboardingStep &&
    screenshotData &&
    !loading &&
    !isTransitioning &&
    !tutorialRunLoading &&
    (!currentTutorialStep?.scope || currentTutorialStep.scope === 'viewer' || currentTutorialStep.scope === 'global');

  // Allow evaluations during tutorial if the current step requires it
  const tutorialAllowsEvaluation = Boolean(
    isOnboarding && currentTutorialStep?.expectedEvaluation,
  );
  const evaluationsDisabled = isReadOnly && !tutorialAllowsEvaluation;
  const disableOverdueEvaluations = isProjectOverdue && !tutorialAllowsEvaluation;

  useEffect(() => {
    if (!isOnboarding || !tutorial.state.isActive || !tutorialConfig) {
      return;
    }

    const currentStep = tutorialConfig.steps.find(
      (step) => step.id === tutorial.state.currentStepId,
    );

    if (
      currentStep &&
      currentStep.scope === 'project' &&
      !['inkscape-ambiguous-intro', 'inkscape-ambiguous-details', 'inkscape-ambiguous-conclusion'].includes(currentStep.id)
    ) {
      console.debug('[ScreenshotViewer] Auto-completing project-scoped step inside viewer', {
        stepId: currentStep.id,
        software,
      });
      console.debug('[ScreenshotViewer] Viewer detected project-scoped step, auto advancing', {
        stepId: currentStep.id,
        software,
      });
      tutorial.completeStep(currentStep.id);
    }
  }, [isOnboarding, tutorial, tutorialConfig, software, tutorial.state.currentStepId]);

  // Sync onboarding step with tutorial context
  useEffect(() => {
    if (isOnboarding && tutorial.state.isActive) {
      setOnboardingStep(tutorial.state.currentStepId);
    } else if (!isOnboarding) {
      setOnboardingStep('');
    }
  }, [isOnboarding, tutorial.state.isActive, tutorial.state.currentStepId]);

  // Reset evaluation when moving to a new tutorial task
  useEffect(() => {
    if (isOnboarding && tutorial.state.isActive) {
      setEvaluation(null);
      setIsBadTask(false);
    }
  }, [isOnboarding, tutorial.state.currentTaskIndex]);

  
  // Check if mistake has been acknowledged when mistakeInfo becomes available
  useEffect(() => {
    const checkAcknowledgedStatus = async () => {
      if (viewingMistake && mistakeInfo?.humanLabelId) {
        try {
          const acknowledgments = await getUserAcknowledgments();
          const acknowledgment = acknowledgments.find(ack => ack.human_label_id === mistakeInfo.humanLabelId);
          if (acknowledgment) {
            setHasAcknowledgedMistake(true);
            setAcknowledgmentId(acknowledgment.id);
          } else {
            setHasAcknowledgedMistake(false);
            setAcknowledgmentId(null);
          }
        } catch (error) {
          console.error('Error checking acknowledged mistakes:', error);
          setHasAcknowledgedMistake(false);
          setAcknowledgmentId(null);
        }
      }
    };
    
    checkAcknowledgedStatus();
  }, [viewingMistake, mistakeInfo]);
  
  // Fetch human label notes when viewing a mistake
  useEffect(() => {
    const fetchMistakeNotes = async () => {
      if (viewingMistake && mistakeInfo?.humanLabelId && runId) {
        try {
          const projectIdForFetch = getProjectIdFromUrl() || effectiveProjectId;
          const labels = await fetchLabelsForRun(runId, projectIdForFetch);
          const humanLabel = labels.find(label => label.id === mistakeInfo.humanLabelId);
          if (humanLabel) {
            setMistakeLabelNotes(humanLabel.notes);
          } else {
            setMistakeLabelNotes(null);
          }
        } catch (error) {
          console.error('Error fetching mistake label notes:', error);
          setMistakeLabelNotes(null);
        }
      }
    };
    
    fetchMistakeNotes();
  }, [viewingMistake, mistakeInfo, runId, effectiveProjectId]);
  
  // Reset evaluation state when starting a new task
  useEffect(() => {
    if (isOnboarding && tutorial.state.isActive) {
      // Only reset when the task index changes (not on every step)
      setEvaluation(null);
      setIsBadTask(false);
      setLabelReason('');
    }
  }, [isOnboarding, tutorial.state.isActive, currentOnboardingTask]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const userId = localStorage.getItem('userId') || '';

  // State to track if we're transitioning to a new problem
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prevProblemRef = useRef<string>('');
  const prevRunRef = useRef<string>('');

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      if (mainContentRef.current?.requestFullscreen) {
        mainContentRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Show first-time tip for downloading files via right-click
  useEffect(() => {
    if (isOnboarding || !userId) {
      return;
    }

    const tipKey = `first_time_download_tip_shown_${userId}`;
    const hasSeenTip = localStorage.getItem(tipKey) === 'true';

    if (!hasSeenTip && !loading && screenshotData && screenshotData.screenshots.length > 0) {
      setShowFirstTimeTip(true);
    }
  }, [isOnboarding, userId, loading, screenshotData]);

  const handleDismissFirstTimeTip = useCallback(() => {
    if (userId) {
      localStorage.setItem(`first_time_download_tip_shown_${userId}`, 'true');
    }
    setShowFirstTimeTip(false);
  }, [userId]);

  const handleDismissVideoComparisonTip = useCallback(() => {
    localStorage.setItem('video_comparison_tip_shown', 'true');
    setShowVideoComparisonTip(false);
  }, []);

  // Show video comparison tip when screenshots are loaded for the first time
  useEffect(() => {
    const hasSeenTip = localStorage.getItem('video_comparison_tip_shown') === 'true';
    if (!hasSeenTip && !loading && !isOnboarding && screenshotData && screenshotData.screenshots.length > 0) {
      setShowVideoComparisonTip(true);
    }
  }, [isOnboarding, loading, screenshotData]);

  useEffect(() => {
    // Check if we're navigating to a different problem/run
    const isNewProblem = prevProblemRef.current !== problemName || prevRunRef.current !== runId;
    
    
    if (isNewProblem && prevProblemRef.current !== '') {
      // We're transitioning to a new problem (not initial load)
      setIsTransitioning(true);
      
      // Clear screenshot data to force reload for the new task
      setScreenshotData(null);
      setProblemRunNotCompleted(false);
      
      // During onboarding, check if user clicked Next task when instructed
      if (isOnboarding) {
        const taskCompleteSteps = ['task-1-complete', 'task-2-complete', 'task-3-complete', 'task-4-complete', 'task-5-complete', 'task-6-complete'];
        if (taskCompleteSteps.includes(onboardingStep)) {
          // User clicked Next task as instructed, advance onboarding
          handleOnboardingStepComplete(onboardingStep);
        }
      }
    }
    
    prevProblemRef.current = problemName;
    prevRunRef.current = runId;
    
    // Reset label ID when changing problems
    setCurrentLabelId(null);
    
    // Reset current index to 0 when changing problems
    if (isNewProblem) {
      setCurrentIndex(0);
    }

    // Load all data in parallel
    const loadAllData = async () => {

      try {
        if (!isOnboarding) {
          // Load all data in parallel
          await Promise.all([
            loadScreenshots(),
            loadExistingLabel()
          ]);
          
          // Check for existing disputes from the current user
          // Only consider pending disputes as blocking acknowledgment
          // Rejected disputes should allow acknowledgment
          try {
            const disputes = await fetchProblemRunDisputes(runId);
            const userId = localStorage.getItem('userId');
            const userDispute = disputes.find(dispute => dispute.user_id === userId && dispute.status === 'pending');
            if (userDispute) {
              setHasExistingDispute(true);
              setUserDisputeId(userDispute.id);
            } else {
              setHasExistingDispute(false);
              setUserDisputeId(null);
            }
          } catch (error) {
            console.error('Error checking for existing disputes:', error);
            setHasExistingDispute(false);
            setUserDisputeId(null);
          }
          
          // Don't check acknowledged status here - we'll do it in a separate effect
        } else {
          // Just load screenshots for onboarding
          await loadScreenshots();
          setHasExistingDispute(false);
        }
      } catch (error: any) {
        console.error('Error loading data:', error);
      } finally {
        // Clear transitioning state only after all data is loaded
        setIsTransitioning(false);
      }
    };
    
    // Only load data if we have a valid problem/run and run is not in pending/running/failed state
    if (problemName && runId && runStatus !== "pending" && runStatus !== "running" && runStatus !== "failed") {
      loadAllData();
    } else {
      setIsTransitioning(false);
      setLoading(false);
    }
    
    // Start autoplay when navigating to a new problem (but not for onboarding)
    if (!isOnboarding) {
      setIsPlaying(true);
    } else {
      // Ensure video is paused for onboarding
      setIsPlaying(false);
    }
    
    // Focus the viewer div to ensure keyboard events work
    setTimeout(() => {
      if (viewerRef.current && !document.activeElement?.matches('input, textarea, select')) {
        viewerRef.current.focus();
      }
    }, 100);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [problemName, runId, isOnboarding, onboardingMessages.length]);


  useEffect(() => {
    if (isPlaying && screenshotData && screenshotData.screenshots.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= screenshotData.screenshots.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 2000 / playbackSpeed); // Adjust speed based on playbackSpeed
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, screenshotData, playbackSpeed]);


  // Ensure currentIndex is within bounds when screenshotData changes
  useEffect(() => {
    if (screenshotData && screenshotData.screenshots.length > 0) {
      if (currentIndex >= screenshotData.screenshots.length) {
        setCurrentIndex(0);
      }
    }
  }, [screenshotData, currentIndex]);


  const loadScreenshots = async () => {
    console.log('[loadScreenshots] Called with:', { problemName, runId, loading, error, screenshotData: !!screenshotData, isOnboarding, software });
    
    // Don't load if we're in a transitioning state without proper data
    if (!problemName || !runId || runId === "loading") {
      setLoading(false); // Important: set loading to false when skipping
      return;
    }

    // Audacity tutorial uses a read-only walkthrough without backend runs
    // Check preload service cache first
    const cachedData = preloadService.get(runId);
    if (cachedData) {
      // Check if this is a "problem run not completed" response
      if ((cachedData as any).__problemRunNotCompleted) {
        setError(null);
        setProblemRunNotCompleted(true);
        const originalDetail = (cachedData as any).__originalDetail;
        if (originalDetail && typeof originalDetail === 'string') {
          const statusMatch = originalDetail.match(/status: (\w+)/);
          if (statusMatch && statusMatch[1]) {
            console.log('Problem run not completed, status:', statusMatch[1]);
          }
        }
        setLoading(false);
        return;
      }
      
      setScreenshotData(cachedData);
      setProblemRunNotCompleted(false);
      setLoading(false);
      
      // Notify parent that screenshots have been loaded from cache
      if (onScreenshotsLoaded && !isOnboarding) {
        console.log('[loadScreenshots] Loaded from cache, calling onScreenshotsLoaded callback');
        onScreenshotsLoaded();
      }
      
      return;
    }
    
    // Check if data is already being preloaded
    const preloadPromise = preloadService.getPromise(runId);
    if (preloadPromise) {
      setLoading(true);
      setError(null);
      try {
        const data = await preloadPromise;
        // Check if this is a "problem run not completed" response
        if ((data as any).__problemRunNotCompleted) {
          setError(null);
          setProblemRunNotCompleted(true);
          const originalDetail = (data as any).__originalDetail;
          if (originalDetail && typeof originalDetail === 'string') {
            const statusMatch = originalDetail.match(/status: (\w+)/);
            if (statusMatch && statusMatch[1]) {
              console.log('Problem run not completed, status:', statusMatch[1]);
            }
          }
          setLoading(false);
          return;
        }
        
        setScreenshotData(data);
        setProblemRunNotCompleted(false);
        // Set initial index for tutorials
        const startIndex = isOnboarding && currentOnboardingTask === 0 ? 
          (software === 'freecad' ? 6 : software === 'musescore' ? 7 : 0) : 0;
        setCurrentIndex(startIndex);
        if (!isOnboarding) {
          setIsPlaying(true);
        }
      } catch (err: any) {
        console.error('Error waiting for preload:', err);
        setError(err.message || 'Failed to load screenshots');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Always set loading when fetching new data
    setLoading(true);
    setError(null);
    try {
      let data;
      
      if (isOnboarding && onboardingMessages && onboardingMessages.length > 0) {
        // For FreeCAD onboarding, create screenshots from the messages
        
        // Convert messages to screenshot format
        const screenshots: Screenshot[] = [];
        let screenshotIndex = 0;
        
        // Process messages to extract screenshots and actions
        onboardingMessages.forEach((msg: any, msgIndex: number) => {
          if (msg.role === 'user' && msg.content) {
            msg.content.forEach((content: any) => {
              if (content.type === 'tool_result' && content.content) {
                // Check if content array contains image
                content.content.forEach((item: any) => {
                  if (item.type === 'image') {
                    // Get the previous assistant message for action details
                    let action = 'screenshot';
                    let description = 'Screenshot';
                    let coordinate = null;
                    let startCoordinate = null;
                    let endCoordinate = null;
                    let text = null;
                    
                    if (msgIndex > 0) {
                      const prevMsg = onboardingMessages[msgIndex - 1];
                      if (prevMsg.role === 'assistant' && prevMsg.content) {
                        // Look for tool_use to get action
                        const toolUse = prevMsg.content.find((c: any) => c.type === 'tool_use');
                        if (toolUse?.input?.action) {
                          action = toolUse.input.action;
                          description = `${action} action`;
                          
                          // Extract coordinates
                          if (toolUse.input.coordinate) {
                            coordinate = toolUse.input.coordinate;
                          }
                          if (toolUse.input.startCoordinate && toolUse.input.endCoordinate) {
                            startCoordinate = toolUse.input.startCoordinate;
                            endCoordinate = toolUse.input.endCoordinate;
                          }
                          // Extract text for type/key actions
                          if (toolUse.input.text) {
                            text = toolUse.input.text;
                          }
                        }
                      }
                    }
                    
                    // For onboarding, use the base64 data directly from messages
                    screenshots.push({
                      index: screenshotIndex++,
                      action: action,
                      description: description,
                      input: { 
                        action: action,
                        coordinate: coordinate,
                        startCoordinate: startCoordinate,
                        endCoordinate: endCoordinate,
                        text: text
                      },
                      screenshot: item.source?.data || '', // Use the base64 data from the message
                      timestamp: new Date().toISOString()
                    });
                  }
                });
              }
            });
          }
        });
        
        data = {
          problem_name: problemName,
          run_id: runId,
          total_screenshots: screenshots.length,
          screenshots
        };
      } else if (isOnboarding && software === 'audacity') {
        data = await fetchScreenshots(problemName, runId, {
          tutorialReadonly: true,
        });
      } else if (isOnboarding && software === 'blender') {
        // For Blender onboarding, fetch from API like normal runs
        data = await fetchScreenshots(problemName, runId, {
          tutorialReadonly: true,
        });
      } else if (isOnboarding && software === 'musescore') {
        // For MuseScore onboarding, fetch from API like normal runs
        data = await fetchScreenshots(problemName, runId, {
          tutorialReadonly: true,
        });
      } else if (isOnboarding && software === 'kicad') {
        // For KiCad onboarding, fetch from API like normal runs
        data = await fetchScreenshots(problemName, runId, {
          tutorialReadonly: true,
        });
      } else if (isOnboarding && software === 'freecad') {
        // Only load FreeCAD data when explicitly in FreeCAD tutorial
        data = await fetchScreenshots('apply-symmetry-constraint-to-stadium-no-axes', '8336268e-4b95-4862-87e4-de9a9736824b');
      } else {
        // Normal non-onboarding run
        data = await fetchScreenshots(problemName, runId, {
          tutorialReadonly: isReadOnly || isOnboarding,
        });
      }
      // Check if this is a "problem run not completed" response
      if ((data as any).__problemRunNotCompleted) {
        setError(null);
        setProblemRunNotCompleted(true);
        const originalDetail = (data as any).__originalDetail;
        if (originalDetail && typeof originalDetail === 'string') {
          const statusMatch = originalDetail.match(/status: (\w+)/);
          if (statusMatch && statusMatch[1]) {
            console.log('Problem run not completed, status:', statusMatch[1]);
          }
        }
        setLoading(false);
        return;
      }
      
      setScreenshotData(data);
      setProblemRunNotCompleted(false);
      // Start at frame 7 (index 6) for FreeCAD, frame 8 (index 7) for MuseScore, frame 1 (index 0) for others
      const startIndex = isOnboarding && currentOnboardingTask === 0 ? 
        (software === 'freecad' ? 6 : software === 'musescore' ? 7 : 0) : 0;
      setCurrentIndex(startIndex);
      // Don't auto-start playback during onboarding
      if (!isOnboarding) {
        setIsPlaying(true);
      }
      
      // Notify parent that screenshots have been loaded successfully
      if (onScreenshotsLoaded && !isOnboarding) {
        console.log('[loadScreenshots] Successfully loaded screenshots, calling onScreenshotsLoaded callback');
        onScreenshotsLoaded();
      }
    } catch (err: any) {
      console.error('Error in loadScreenshots:', err);
      setError(err.message || 'Failed to load screenshots');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingLabel = async () => {
    try {
      // Always re-parse URL for the most current project ID
      const currentUrlProjectId = getProjectIdFromUrl();
      const projectIdForFetch = currentUrlProjectId || effectiveProjectId;
      
      const labels = await fetchLabelsForRun(runId, projectIdForFetch);
      // Find if current user has already labeled this run
      const userLabel = labels.find(label => label.user_id === userId);
      if (userLabel) {
        // Check if the label is soft-deleted AND from the current project
        if (userLabel.deleted_at && userLabel.labeling_project_id === projectIdForFetch) {
          setUserLabelDeleted(true);
          setEvaluation(null);
          setIsBadTask(false);
          setLabelReason('');
          setCurrentLabelId(null);
        } else if (!userLabel.deleted_at) {
          const { reason, isBadTask: badTask } = parseLabelNotes(userLabel.notes);
          setEvaluation(userLabel.label);
          setIsBadTask(badTask);
          setLabelReason(reason);
          setCurrentLabelId(userLabel.id);
          setUserLabelDeleted(false);
        } else {
          // Soft-deleted but from a different project - treat as no label
          setEvaluation(null);
          setIsBadTask(false);
          setLabelReason('');
          setCurrentLabelId(null);
          setUserLabelDeleted(false);
        }
      } else {
        setEvaluation(null);
        setIsBadTask(false);
        setLabelReason('');
        setCurrentLabelId(null);
        setUserLabelDeleted(false);
      }
    } catch (err) {
      // If no labels exist, that's fine
      setEvaluation(null);
      setIsBadTask(false);
      setLabelReason('');
      setCurrentLabelId(null);
      setUserLabelDeleted(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
      setIsPlaying(false);
      setNavigationIndicator('backward');
      setIndicatorKey(prev => prev + 1);
      
      // During onboarding, track action only for specific steps
      if (isOnboarding && onboardingStep === 'seek-backward' && currentOnboardingTask === 0) {
        handleOnboardingStepComplete('seek-backward');
      }
    }
  };

  const handleNext = () => {
    if (screenshotData && currentIndex < screenshotData.screenshots.length - 1) {
      setCurrentIndex((prev) =>
        Math.min(screenshotData.screenshots.length - 1, prev + 1)
      );
      setIsPlaying(false);
      setNavigationIndicator('forward');
      setIndicatorKey(prev => prev + 1);
      
      // During onboarding, track action only for specific steps
      if (isOnboarding && currentOnboardingTask === 0) {
        if (onboardingStep === 'skip-to-frame-2' || onboardingStep === 'go-to-frame-3') {
          handleOnboardingStepComplete(onboardingStep);
        }
      }
    }
  };

  const handleOnboardingStepComplete = (stepId: string) => {
    console.debug('[ScreenshotViewer] Completing tutorial step', {
      stepId,
      isOnboarding,
      tutorialActive: tutorial.state.isActive,
      tutorialStep: tutorial.state.currentStepId,
      software,
    });
    // Complete the step in the tutorial context
    tutorial.completeStep(stepId);
    const advanceSteps = new Set([
      'audacity-example-general-info',
      'audacity-terminal-conclusion',
      'audacity-combo-conclusion',
      'audacity-code-fail-conclusion',
      'audacity-precision-conclusion',
      'audacity-judgement-conclusion',
      'shotcut-obvious-failure-conclusion',
      'shotcut-obvious-success-conclusion',
      'shotcut-bad-task-conclusion',
      'shotcut-ffmpeg-conclusion',
      'shotcut-timing-conclusion',
      'gimp-obvious-success-conclusion',
      'gimp-obvious-failure-conclusion',
      'gimp-deceptive-failure-conclusion',
      'gimp-deceptive-success-conclusion',
      'gimp-judgement-success-conclusion',
      'gimp-judgement-failure-conclusion',
      'libreoffice-obvious-success-conclusion',
      'libreoffice-obvious-failure-conclusion',
      'libreoffice-deceptive-success-1-conclusion',
      'libreoffice-deceptive-success-2-conclusion',
      'libreoffice-deceptive-failure-conclusion',
      'libreoffice-ambiguous-task-conclusion',
      'inkscape-obvious-success-conclusion',
      'inkscape-obvious-failure-conclusion',
      'inkscape-hidden-success-conclusion',
      'inkscape-hidden-failure-conclusion',
      'inkscape-precision-conclusion',
      'inkscape-ambiguous-conclusion',
    ]);
    if (advanceSteps.has(stepId)) {
      console.debug('[ScreenshotViewer] Advancing to next tutorial task', {
        completedStep: stepId,
      });
      tutorial.nextTask();
    }

    // Handle special actions for certain steps
    if (stepId === 'tutorial-complete') {
      // Tutorial is complete
      onOnboardingComplete && onOnboardingComplete();
    }
  };
  
  // Watch for step changes from tutorial context and handle special actions
  useEffect(() => {
    if (tutorial.state.currentStepId === 'finalframe' && screenshotData) {
      // Jump to final frame with a small delay to ensure proper state updates
      setTimeout(() => {
        setCurrentIndex(screenshotData.screenshots.length - 1);
        setIsPlaying(false);
      }, 100);
    }
  }, [tutorial.state.currentStepId, screenshotData]);

  const handleOnboardingComplete = () => {
    setOnboardingStep('');
    if (onOnboardingComplete) {
      onOnboardingComplete();
    }
  };

  const handleOnboardingStepPrevious = (stepId: string) => {
    console.debug('[ScreenshotViewer] Going to previous tutorial step', {
      stepId,
      isOnboarding,
      tutorialActive: tutorial.state.isActive,
      tutorialStep: tutorial.state.currentStepId,
      software,
    });
    // Go to the previous step in the tutorial context
    tutorial.previousStep(stepId);
  };

  const handlePlayPause = () => {
    setIsPlaying(prev => {
      const newIsPlaying = !prev;
      setNavigationIndicator(newIsPlaying ? 'play' : 'pause');
      setIndicatorKey(prev => prev + 1);
      
      // During onboarding, track action only for specific steps
      if (isOnboarding && currentOnboardingTask === 0) {
        if (onboardingStep === 'play' && newIsPlaying) {
          handleOnboardingStepComplete('play');
        } else if (onboardingStep === 'pause' && !newIsPlaying) {
          handleOnboardingStepComplete('pause');
        }
      }
      
      // If we're at the end and paused, reset to beginning when play is pressed
      if (newIsPlaying && screenshotData && currentIndex >= screenshotData.screenshots.length - 1) {
        setCurrentIndex(0);
      }
      return newIsPlaying;
    });
  };

  const handleSpeedChange = () => {
    setPlaybackSpeed((prev) => {
      if (prev === 1) return 2;
      if (prev === 2) return 4;
      return 1;
    });
    
    // During onboarding, track action only for specific steps
    if (isOnboarding && currentOnboardingTask === 0 && onboardingStep === 'speed') {
      handleOnboardingStepComplete('speed');
    }
  };

  const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // During onboarding, timeline scrubbing is always allowed to let users explore
    
    const newIndex = parseInt(e.target.value, 10);
    setCurrentIndex(newIndex);
    setIsPlaying(false);
    
    // Don't auto-advance the tutorial when scrubbing timeline
    // User needs to click Next button to continue
    
    // Refocus the viewer to ensure keyboard shortcuts work
    setTimeout(() => {
      if (viewerRef.current) {
        viewerRef.current.focus();
      }
    }, 0);
  };

  const handleScreenshotClick = (e: React.MouseEvent) => {
    // Only handle left click
    if (e.button === 0) {
      handlePlayPause();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleDownloadModelOutput = async () => {
    setContextMenu(null);
    if (!runId) {
      alert('Cannot download model output: run ID is missing.');
      return;
    }

    try {
      await downloadModelOutputFile(runId, {
        onStart: (filename) => {
          if (downloadToastTimeoutRef.current !== null) {
            window.clearTimeout(downloadToastTimeoutRef.current);
            downloadToastTimeoutRef.current = null;
          }
          setDownloadFilename(filename);
          setDownloadProgress(0);
        },
        onProgress: (progress) => {
          if (typeof progress === 'number') {
            setDownloadProgress(progress);
          }
        },
      });

      if (downloadToastTimeoutRef.current !== null) {
        window.clearTimeout(downloadToastTimeoutRef.current);
      }
      downloadToastTimeoutRef.current = window.setTimeout(() => {
        handleCloseDownloadToast();
      }, 3000);
    } catch (err) {
      console.error('Failed to download model output artifact:', err);
      handleCloseDownloadToast();
      alert('Failed to download model output. The file may not be available.');
    }
  };

  const handleDownloadStartState = async () => {
    setContextMenu(null);
    if (!problemId) {
      alert('Cannot download start state: problem ID is missing.');
      return;
    }

    try {
      await downloadStartState(problemId, runId, {
        onStart: (filename) => {
          if (downloadToastTimeoutRef.current !== null) {
            window.clearTimeout(downloadToastTimeoutRef.current);
            downloadToastTimeoutRef.current = null;
          }
          setDownloadFilename(filename);
          setDownloadProgress(0);
        },
        onProgress: (progress) => {
          if (typeof progress === 'number') {
            setDownloadProgress(progress);
          }
        },
      });

      if (downloadToastTimeoutRef.current !== null) {
        window.clearTimeout(downloadToastTimeoutRef.current);
      }
      downloadToastTimeoutRef.current = window.setTimeout(() => {
        handleCloseDownloadToast();
      }, 3000);
    } catch (err) {
      console.error('Failed to download start state:', err);
      handleCloseDownloadToast();
      alert('Failed to download start state. The file may not be available.');
    }
  };

  const handleCompareVideos = () => {
    setContextMenu(null);
    setShowVideoComparisonModal(true);
  };

  const handleEvaluation = async (result: LabelType) => {
    if (submittingLabel) return;
    // Allow evaluation if tutorial step requires it
    if (isReadOnly && !tutorialAllowsEvaluation) return;

    // During onboarding or if clicking to deselect, skip modal
    const isDeselecting = evaluation === result && !isBadTask;

    let currentStep: TutorialStep | undefined;
    if (software) {
      const tutorialConfig = getTutorialConfig(software);
      currentStep = tutorialConfig?.steps.find((step) => step.id === onboardingStep);
    }

    // Show modal for reason (not during onboarding and not when deselecting)
    if (!isOnboarding && !isDeselecting) {
      setPendingEvaluation({
        type: result === LabelType.SUCCESS ? 'success' : 'fail',
        labelType: result
      });
      setModalReasonInput(labelReason || '');
      setShowReasonModal(true);
      return;
    }

    // During onboarding, check if this action is allowed
    if (isOnboarding && software && currentStep) {
      // If current step is an evaluation step, handle it
      if (currentStep.action === 'evaluate') {
        // Check if the step has an expected evaluation
        if (currentStep.expectedEvaluation) {
          const expectedResult = currentStep.expectedEvaluation === 'success' ? LabelType.SUCCESS :
                                 currentStep.expectedEvaluation === 'fail' ? LabelType.FAILED :
                                 LabelType.FAILED; // 'bad-task' maps to FAILED
          
          // If this is NOT the expected answer and NOT a bad task expectation
          if (result !== expectedResult && currentStep.expectedEvaluation !== 'bad-task') {
            setShowWrongEvaluationDialog(true);
            return;
          }
          
          // If expecting bad task but user clicked Success/Fail (not bad task button)
          if (currentStep.expectedEvaluation === 'bad-task' && result !== LabelType.FAILED) {
            setShowWrongEvaluationDialog(true);
            return;
          }
        }
        
        // Legacy FreeCAD validation for backward compatibility
        if (software === 'freecad' && !currentStep.expectedEvaluation) {
          // Task 2: Should be FAILED
          if (currentOnboardingTask === 1 && result !== LabelType.FAILED) {
            setShowWrongEvaluationDialog(true);
            return;
          }
          // Task 3: Should be FAILED
          if (currentOnboardingTask === 2 && result !== LabelType.FAILED) {
            setShowWrongEvaluationDialog(true);
            return;
          }
          // Task 5: Should be Bad Task (not Success/Fail)
          if (currentOnboardingTask === 4 && result !== LabelType.FAILED) {
            setShowWrongEvaluationDialog(true);
            return;
          }
        }
        
        // Correct answer - set the evaluation state so the button shows as selected
        setEvaluation(result);
        setIsBadTask(false);
        setCompletedEvaluationSteps((prev) => {
          const next = new Set(prev);
          next.add(currentStep!.id);
          return next;
        });
        return;
      }
    }
    
    // If clicking the same evaluation AND not a bad task, deselect it
    if (evaluation === result && !isBadTask) {
      // Delete from backend if we have a label ID
      if (currentLabelId && !isOnboarding) {
        setSubmittingLabel(true);
        try {
          await deleteLabel(currentLabelId);
          setCurrentLabelId(null);
        } catch (error: any) {
          console.error('Failed to delete label:', error);
          alert('Failed to remove evaluation. Please try again.');
          setSubmittingLabel(false);
          return;
        }
        setSubmittingLabel(false);
      }
      
      setEvaluation(null);
      setIsBadTask(false);
      setLabelReason('');
      if (currentStep?.action === 'evaluate') {
        setCompletedEvaluationSteps((prev) => {
          if (!prev.has(currentStep!.id)) return prev;
          const next = new Set(prev);
          next.delete(currentStep!.id);
          return next;
        });
      }
      if (!isOnboarding && onRunLabelChange) {
        onRunLabelChange(runId, null);
      }
      return;
    }
    
    
    // During onboarding, don't submit to backend
    if (isOnboarding) {
      setEvaluation(result);
      setIsBadTask(false);
      if (currentStep?.action === 'evaluate') {
        setCompletedEvaluationSteps((prev) => {
          const next = new Set(prev);
          next.add(currentStep!.id);
          return next;
        });
      }
      return;
    }
    
    setSubmittingLabel(true);
    try {
      // Submit to backend (will create or update)
      let finalProjectId = effectiveProjectId;
      
      // Ensure we have a project ID when labeling in project context
      if (!viewingMistake && !isOnboarding) {
        // Re-parse URL at the moment of labeling to ensure we have the latest
        const currentUrlProjectId = getProjectIdFromUrl();
        finalProjectId = currentUrlProjectId || effectiveProjectId || projectId;
        
        if (!isInProjectLabelingContext()) {
          console.error('Not in proper project labeling URL. URL:', window.location.pathname);
          throw new Error('Labels can only be created from /project/{id}/runs/{index} URLs');
        }
        if (!finalProjectId) {
          console.error('No project ID found for labeling.', {
            url: window.location.pathname,
            currentUrlProjectId,
            effectiveProjectId,
            projectId
          });
          throw new Error('Cannot create label without project context');
        }
      }
      
      const label = await createLabel(runId, userId, result, undefined, labelReason, finalProjectId);
      setEvaluation(result);
      setIsBadTask(false);
      setCurrentLabelId(label.id);
      if (!isOnboarding && onRunLabelChange) {
        onRunLabelChange(runId, label);
      }
    } catch (error: any) {
      console.error('Failed to submit label:', error);
      alert('Failed to submit evaluation. Please try again.');
    } finally {
      setSubmittingLabel(false);
    }
  };

  const handleReasonSubmit = async () => {
    if (isReadOnly) {
      setShowReasonModal(false);
      setPendingEvaluation(null);
      return;
    }

    if (!pendingEvaluation || !modalReasonInput.trim()) {
      alert('Please provide a reason for your evaluation');
      return;
    }

    // Save the reason and close modal
    setLabelReason(modalReasonInput);
    setShowReasonModal(false);

    // Now proceed with the evaluation
    const { type, labelType } = pendingEvaluation;
    setPendingEvaluation(null);

    setSubmittingLabel(true);
    try {
      let finalProjectId = effectiveProjectId;

      // Ensure we have a project ID when labeling in project context
      if (!viewingMistake && !isOnboarding) {
        const currentUrlProjectId = getProjectIdFromUrl();
        finalProjectId = currentUrlProjectId || effectiveProjectId || projectId;

        if (!isInProjectLabelingContext()) {
          console.error('Not in proper project labeling URL. URL:', window.location.pathname);
          throw new Error('Labels can only be created from /project/{id}/runs/{index} URLs');
        }
        if (!finalProjectId) {
          console.error('No project ID found for labeling.', {
            url: window.location.pathname,
            currentUrlProjectId,
            effectiveProjectId,
            projectId
          });
          throw new Error('Cannot create label without project context');
        }
      }

      // Create label with reason
      const notes = type === 'bad-task' ? `BAD_TASK: ${modalReasonInput}` : modalReasonInput;
      const label = await createLabel(runId, userId, labelType, undefined, notes, finalProjectId);

      setEvaluation(labelType);
      setIsBadTask(type === 'bad-task');
      setCurrentLabelId(label.id);
      if (!isOnboarding && onRunLabelChange) {
        onRunLabelChange(runId, label);
      }
    } catch (error: any) {
      console.error('Failed to submit label:', error);
      alert('Failed to submit evaluation. Please try again.');
    } finally {
      setSubmittingLabel(false);
    }
  };

  const handleIndicatorComplete = () => {
    setNavigationIndicator(null);
  };

  const handleDisputeSubmit = async (proposedScore: 'success' | 'failed' | 'bad_task', note: string) => {
    try {
      const disputeData: ScoreDisputeCreate = {
        problem_run_id: runId,
        note,
        proposed_score: proposedScore
      };
      
      const newDispute = await createScoreDispute(disputeData);
      // Invalidate the cache for this problem run's disputes
      invalidateAPICache(`/problem-runs/${runId}/score-disputes`);
      alert('Your dispute has been submitted successfully. We will review it and update your accuracy if appropriate.');
      setShowDisputeModal(false);
      setHasExistingDispute(true);
      if (newDispute?.id) {
        setUserDisputeId(newDispute.id);
      }
    } catch (error: any) {
      console.error('Error submitting dispute:', error);
      throw error; // Let the modal handle the error
    }
  };

  const handleAcknowledgeMistake = async () => {
    if (acknowledgingMistake || hasAcknowledgedMistake || hasExistingDispute) return;
    
    if (!mistakeInfo?.humanLabelId) {
      console.error('No humanLabelId available');
      alert('Unable to acknowledge mistake - missing label ID');
      return;
    }
    
    setAcknowledgingMistake(true);
    try {
      const result = await acknowledgeMistake(mistakeInfo.humanLabelId);
      setHasAcknowledgedMistake(true);
      if (result?.id) {
        setAcknowledgmentId(result.id);
      }
      // Invalidate relevant caches
      invalidateAPICache('/me/incorrect-labels');
      invalidateAPICache('/human-labels/mistakes/acknowledged-ids');
    } catch (error: any) {
      console.error('Error acknowledging mistake:', error);
      alert('Failed to acknowledge mistake. Please try again.');
    } finally {
      setAcknowledgingMistake(false);
    }
  };

  const handleDeleteClick = (type: 'dispute' | 'acknowledgment') => {
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteType) return;

    setIsDeleting(true);
    try {
      if (deleteType === 'dispute' && userDisputeId) {
        await deleteDispute(userDisputeId);
        setHasExistingDispute(false);
        setUserDisputeId(null);
        // Invalidate relevant caches
        invalidateAPICache('/score-disputes/user');
        invalidateAPICache('/me/incorrect-labels');
      } else if (deleteType === 'acknowledgment' && acknowledgmentId) {
        await deleteMistakeAcknowledgment(acknowledgmentId);
        setHasAcknowledgedMistake(false);
        setAcknowledgmentId(null);
        // Invalidate relevant caches
        invalidateAPICache('/me/incorrect-labels');
        invalidateAPICache('/human-labels/mistakes/acknowledged-ids');
      }
      setShowDeleteModal(false);
    } catch (error: any) {
      console.error(`Error deleting ${deleteType}:`, error);
      alert(`Failed to delete ${deleteType}. Please try again.`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseCompletionDialog = () => {
    setShowCompletionDialog(false);
    if (onBackToProjects) {
      onBackToProjects();
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check if user is typing in an input, textarea, select, or contenteditable element
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || 
                      target.tagName === 'TEXTAREA' || 
                      target.tagName === 'SELECT' ||
                      target.contentEditable === 'true' ||
                      target.getAttribute('role') === 'textbox';
      
      // Also check for modifier keys
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey || e.shiftKey;
      
      if (!isTyping && !hasModifier) {
        switch (e.key) {
          case ' ':
          case 'Space':
            e.preventDefault(); // Prevent page scroll
            e.stopPropagation(); // Stop event from bubbling
            handlePlayPause();
            break;
          case 'ArrowLeft':
          case 'Left':
            e.preventDefault();
            e.stopPropagation();
            handlePrevious();
            break;
          case 'ArrowRight':
          case 'Right':
            e.preventDefault();
            e.stopPropagation();
            handleNext();
            break;
          case 'ArrowUp':
          case 'Up':
            e.preventDefault();
            e.stopPropagation();
            // Increase speed
            setPlaybackSpeed((prev) => {
              if (prev === 1) return 2;
              if (prev === 2) return 4;
              return 4; // Max speed
            });
            // Track onboarding completion if needed
            if (isOnboarding && currentOnboardingTask === 0 && onboardingStep === 'speed') {
              handleOnboardingStepComplete('speed');
            }
            break;
          case 'ArrowDown':
          case 'Down':
            e.preventDefault();
            e.stopPropagation();
            // Decrease speed
            setPlaybackSpeed((prev) => {
              if (prev === 4) return 2;
              if (prev === 2) return 1;
              return 1; // Min speed
            });
            // Track onboarding completion if needed
            if (isOnboarding && currentOnboardingTask === 0 && onboardingStep === 'speed') {
              handleOnboardingStepComplete('speed');
            }
            break;
          // Fullscreen shortcut removed
          // case 'f':
          // case 'F':
          //   e.preventDefault();
          //   e.stopPropagation();
          //   toggleFullscreen();
          //   break;
        }
      }
    };

    // Add to multiple targets to ensure we catch the event
    document.addEventListener('keydown', handleKeyPress, true);
    window.addEventListener('keydown', handleKeyPress, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress, true);
      window.removeEventListener('keydown', handleKeyPress, true);
    };
  }, [handlePrevious, handleNext, handlePlayPause, isOnboarding, onboardingStep, toggleFullscreen]);  // Include dependencies

  // Load prompt data when viewing a mistake with needsPromptLoad flag
  useEffect(() => {
    if (needsPromptLoad && viewingMistake && runId && !loadedPrompt) {
      const loadPromptData = async () => {
        try {
          // Fetch the messages to extract the prompt
          const response = await axios.get(
            `${API_BASE_URL}/problem-runs/${runId}/messages`,
            { headers: getAuthHeaders() },
          );

          const messages = response.data;
          const prompt = extractPromptFromMessages(messages);

          setLoadedPrompt(prompt);
        } catch (error) {
          console.error("Error loading prompt:", error);
          setLoadedPrompt("Error loading prompt");
        }
      };

      loadPromptData();
    }
  }, [needsPromptLoad, viewingMistake, runId, loadedPrompt]);

  // Only show loading on initial load, not on navigation
  if (loading && !screenshotData) {
    return (
      <div className="screenshot-viewer">
        <div className="loading-skeleton-container">
          <div className="skeleton-sidebar">
            <div className="skeleton-loader" style={{ width: '100%', height: '120px', marginBottom: '20px' }}></div>
            <div className="skeleton-loader" style={{ width: '100%', height: '60px', marginBottom: '16px' }}></div>
            <div className="skeleton-loader" style={{ width: '100%', height: '200px', marginBottom: '20px' }}></div>
            <div className="skeleton-buttons">
              <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px', display: 'inline-block' }}></div>
              <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px', display: 'inline-block', marginLeft: '4%' }}></div>
            </div>
          </div>
          <div className="skeleton-main">
            <div className="skeleton-loader" style={{ width: '80%', height: '60vh', margin: '0 auto', borderRadius: '8px' }}></div>
            <div className="skeleton-loader" style={{ width: '200px', height: '40px', margin: '20px auto 0', borderRadius: '20px' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Show skeleton loader when loading, transitioning (but only if we expect data)
  if ((loading || isTransitioning) && !error) {
    return (
      <div className="screenshot-viewer">
        <div className="loading-skeleton-container">
          <div className="skeleton-sidebar">
            <div className="skeleton-loader" style={{ width: '100%', height: '120px', marginBottom: '20px' }}></div>
            <div className="skeleton-loader" style={{ width: '100%', height: '60px', marginBottom: '16px' }}></div>
            <div className="skeleton-loader" style={{ width: '100%', height: '200px', marginBottom: '20px' }}></div>
            <div className="skeleton-buttons">
              <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px', display: 'inline-block' }}></div>
              <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px', display: 'inline-block', marginLeft: '4%' }}></div>
            </div>
          </div>
          <div className="skeleton-main">
            <div className="skeleton-loader" style={{ width: '80%', height: '60vh', margin: '0 auto', borderRadius: '8px' }}></div>
            <div className="skeleton-loader" style={{ width: '200px', height: '40px', margin: '20px auto 0', borderRadius: '20px' }}></div>
          </div>
        </div>
      </div>
    );
  }

  
  const currentScreenshot = screenshotData?.screenshots?.[currentIndex];
  const nextScreenshot = screenshotData && currentIndex < screenshotData.screenshots.length - 1 
    ? screenshotData.screenshots[currentIndex + 1] 
    : null;
  const totalActions = screenshotData?.screenshots?.length || 0;
  const thoughtProcessText = (currentScreenshot?.thoughtProcess || '').trim();
  const showImpossibleMarker =
    thoughtProcessText.includes('<answer>[IMPOSSIBLE]</answer>') ||
    thoughtProcessText.includes('It is not possible to complete this task');
  const toolPanelCommand = (currentScreenshot?.toolCommand || '').trim();
  const toolPanelResult = (currentScreenshot?.toolResultText || '').trim();
  const toolPanelResultLabel = currentScreenshot?.isToolError ? 'Error' : 'Result';
  const toolPanelIsError = currentScreenshot?.isToolError ?? false;

  // Guard against undefined screenshot during transitions
  // But don't show skeleton if we have an error or no screenshots data
  if ((!currentScreenshot || isTransitioning) && !error && screenshotData?.screenshots && screenshotData.screenshots.length > 0) {
    return (
      <div className="screenshot-viewer">
        <div className="loading-skeleton-container">
          <div className="skeleton-sidebar">
            <div className="skeleton-loader" style={{ width: '100%', height: '120px', marginBottom: '20px' }}></div>
            <div className="skeleton-loader" style={{ width: '100%', height: '60px', marginBottom: '16px' }}></div>
            <div className="skeleton-loader" style={{ width: '100%', height: '200px', marginBottom: '20px' }}></div>
            <div className="skeleton-buttons">
              <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px', display: 'inline-block' }}></div>
              <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px', display: 'inline-block', marginLeft: '4%' }}></div>
            </div>
          </div>
          <div className="skeleton-main">
            <div className="skeleton-loader" style={{ width: '80%', height: '60vh', margin: '0 auto', borderRadius: '8px' }}></div>
            <div className="skeleton-loader" style={{ width: '200px', height: '40px', margin: '20px auto 0', borderRadius: '20px' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Debug logging for render
  console.log('[ScreenshotViewer] Render state:', {
    loading,
    error,
    screenshotData: !!screenshotData,
    screenshotsLength: screenshotData?.screenshots?.length,
    isTransitioning,
    isOnboarding,
    software,
    problemName,
    runId,
    currentIndex,
    currentScreenshot: !!currentScreenshot,
    showViewerTutorialOverlay,
    tutorialStep: tutorial.state.currentStepId,
  });

  const overlayEvaluationReady = completedEvaluationSteps.has(onboardingStep);

  return (
    <div className="screenshot-viewer" ref={viewerRef} tabIndex={-1}>
      <div className="left-sidebar">
        <div className="sidebar-content-top">
          {/* Only show run info and back button when not in onboarding mode and when we have project context */}
          {(!isOnboarding || isReadOnly) && !viewingMistake && projectId && (
            <div className="sidebar-section run-info-section">
              <button
                className="back-to-problem-btn"
                onClick={() => {
                  if (isOnboarding && tutorial.state.isActive) {
                    const currentStepId = tutorial.state.currentStepId;
                  const returnSteps = new Set([
                    'audacity-viewer-return',
                    'shotcut-viewer-return',
                    'gimp-viewer-return',
                    'libreoffice-viewer-return',
                    'inkscape-viewer-return',
                  ]);
                    if (currentStepId && returnSteps.has(currentStepId)) {
                      tutorial.completeStep(currentStepId);
                    }
                  }
                  // Prefer upstream navigation handler so state stays in sync
                  if (onBackToProjects) {
                    onBackToProjects();
                    return;
                  }

                  const destinationProjectId = effectiveProjectId || projectId;
                  if (destinationProjectId && problemId) {
                    window.location.href = `/project/${destinationProjectId}/problems/${problemId}`;
                  } else if (destinationProjectId) {
                    window.location.href = `/project/${destinationProjectId}`;
                  } else {
                    window.history.back();
                  }
                }}
              >
                ← Back to Problem
              </button>
              <div className="run-info-header">
                <p className="run-info-label">Current Run</p>
                <p className="run-info-current">
                  <span className="run-number">{progressInfo.current}</span> of {progressInfo.total}
                </p>
                <p className="run-info-problem">
                  <span className="run-info-problem-label">Problem:</span> {problemName}
                </p>
              </div>
            </div>
          )}

          <div className="sidebar-section">
            {viewingMistake && problemName && (
              <>
                <h3>Task Name</h3>
                <div className="task-name">
                  {problemName.replace(/^["']|["']$/g, '').trim()}
                </div>
              </>
            )}
            <h3>Task Description</h3>
            <div className="task-description">
              {isTransitioning ? (
                <div className="skeleton-paragraph">
                  <div className="skeleton-loader skeleton-text long"></div>
                  <div className="skeleton-loader skeleton-text medium"></div>
                  <div className="skeleton-loader skeleton-text long"></div>
                  <div className="skeleton-loader skeleton-text short"></div>
                </div>
              ) : (
                <div className="task-description-text">
                  {loadedPrompt || problemPrompt}
                </div>
              )}
            </div>
          </div>

          {/* Open Computer button for Mattermost problems in dispute/mistake viewing mode */}
          {console.log('[ScreenshotViewer] Open Computer conditions:', {
            mattermostState,
            isDisputeMode,
            viewingMistake,
            problemId,
            software,
          })}
          {mattermostState && (isDisputeMode || viewingMistake) && problemId && (
            <div className="sidebar-section">
              <button
                type="button"
                className="open-computer-btn"
                onClick={() => setIsPlaygroundModalOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#4f46e5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <span>💻</span>
                Open Computer
              </button>
            </div>
          )}

          {/* Show label information for soft-deleted labels */}
          {userLabelDeleted && evaluation && (
            <div className="sidebar-section evaluation-section">
              <h3>Your Label (Deleted)</h3>
              <div className="label-info-section">
                <div className="label-info-item">
                  <span className="label-info-type">Your Label:</span>
                  <span className={`label-info-value ${evaluation}`}>
                    {evaluation === LabelType.SUCCESS ? '✓ Success' : '✗ Failed'}
                  </span>
                </div>
                <div className="label-info-date">
                  This label has been soft-deleted
                </div>
              </div>
            </div>
          )}
          
          {viewingMistake && mistakeInfo && (
            <div className="sidebar-section mistake-info-section">
              <h3>Label Information</h3>
              <div className="mistake-label-info">
                <div className="label-info-item">
                  <span className="label-info-type">Your Label:</span>
                  <span className={`label-info-value ${mistakeIsBadTask ? 'bad-task' : mistakeInfo.userLabel}`}>
                    {mistakeIsBadTask ? '✗ Bad Task' :
                     mistakeInfo.userLabel === 'success' ? '✓ Success' : '✗ Failed'}
                  </span>
                </div>
                {mistakeInfo.userLabelNotes && mistakeInfo.userLabelNotes.trim() !== '' && (
                  <div className="label-reason">
                    <div className="label-reason-text">{mistakeInfo.userLabelNotes}</div>
                  </div>
                )}
                <div className="label-info-item">
                  <span className="label-info-type">Actual Result:</span>
                  <span className={`label-info-value ${mistakeInfo.actualResult}`}>
                    {mistakeInfo.actualResult === 'success' ? '✓ Success' : '✗ Failed'}
                  </span>
                </div>
                {(() => {
                  // Get matching reasons from other users who got it right
                  if (!mistakeInfo.allLabels) return null;
                  const correctLabel = mistakeInfo.actualResult;
                  const matchingReasons = mistakeInfo.allLabels
                    .filter(label =>
                      label.label === correctLabel &&
                      label.notes &&
                      label.notes.trim() !== '' &&
                      label.user_id !== mistakeInfo.allLabels?.find(l => l.notes === mistakeInfo.userLabelNotes)?.user_id
                    )
                    .slice(0, 2)
                    .map(label => label.notes);

                  if (matchingReasons.length === 0) return null;

                  return (
                    <div className="label-reasons">
                      <div className="label-reasons-header">
                        Other users who marked it {mistakeInfo.actualResult === 'success' ? 'Success' : 'Failed'}:
                      </div>
                      {matchingReasons.map((reason, idx) => (
                        <div key={idx} className="label-reason">
                          <div className="label-reason-text">{reason}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div className="label-info-date">
                  Labeled on {new Date(mistakeInfo.labeledAt + 'Z').toLocaleString()}
                </div>
              </div>
              {(hasExistingDispute || hasAcknowledgedMistake) && (
                <div className={`mistake-status-banner ${hasExistingDispute ? 'dispute' : 'acknowledged'}`}>
                  {hasExistingDispute ? (
                    <>
                      <span className="status-icon">🚩</span>
                      <span>Dispute submitted</span>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteClick('dispute')}
                        title="Delete dispute"
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="status-icon">✅</span>
                      <span>Mistake acknowledged</span>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteClick('acknowledgment')}
                        title="Delete acknowledgment"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="mistake-action-buttons">
                <button 
                  className={`dispute-button ${hasExistingDispute ? 'disabled' : ''} ${hasAcknowledgedMistake ? 'disabled' : ''}`}
                  onClick={() => !hasExistingDispute && !hasAcknowledgedMistake && setShowDisputeModal(true)}
                  disabled={hasExistingDispute || hasAcknowledgedMistake}
                  title={hasExistingDispute ? 'You have already submitted a dispute for this problem run' : hasAcknowledgedMistake ? 'Cannot dispute after acknowledging' : ''}
                >
                  <span style={{ fontSize: '1rem' }}>🚩</span>
                  {hasExistingDispute ? 'Dispute submitted' : 'Dispute final answer'}
                </button>
                <button 
                  className={`acknowledge-button ${hasAcknowledgedMistake ? 'disabled' : ''} ${hasExistingDispute ? 'disabled' : ''}`}
                  onClick={(e) => {
                    console.log('Acknowledge button clicked!');
                    e.preventDefault();
                    handleAcknowledgeMistake();
                  }}
                  disabled={hasAcknowledgedMistake || hasExistingDispute || acknowledgingMistake}
                  title={hasAcknowledgedMistake ? 'You have already acknowledged this mistake' : hasExistingDispute ? 'Cannot acknowledge after disputing' : ''}
                >
                  <span style={{ fontSize: '1rem' }}>✅</span>
                  {acknowledgingMistake ? 'Acknowledging...' : hasAcknowledgedMistake ? 'Acknowledged' : 'Acknowledge Mistake'}
                </button>
              </div>
            </div>
          )}
          
          {/* Show all disputes when there are multiple, otherwise show single dispute */}
          {allDisputes && allDisputes.length > 1 ? (
            <div className="sidebar-section dispute-info-section">
              <h3>All Disputes ({allDisputes.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {allDisputes.map((dispute, index) => (
                  <div
                    key={dispute.id}
                    style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      cursor: isDisputeMode && onOpenDisputeModal ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (isDisputeMode && onOpenDisputeModal && userPermissions.some(perm =>
                        perm.startsWith('resolve_disputes_') ||
                        (software && perm === `resolve_disputes_${software}`)
                      )) {
                        onOpenDisputeModal(dispute);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px' }}>Dispute {index + 1}</span>
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: dispute.status === 'accepted' ? '#dcfce7' :
                                          dispute.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                          color: dispute.status === 'accepted' ? '#166534' :
                                 dispute.status === 'rejected' ? '#991b1b' : '#92400e',
                        }}
                      >
                        {dispute.status.charAt(0).toUpperCase() + dispute.status.slice(1)}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                      <span style={{ color: '#6b7280' }}>Proposed: </span>
                      <span className={`dispute-proposed-value ${dispute.proposed_score}`}>
                        {dispute.proposed_score === 'success' ? 'Success' :
                         dispute.proposed_score === 'failed' ? 'Failed' : 'Bad Task'}
                      </span>
                    </div>
                    {dispute.final_status && (
                      <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                        <span style={{ color: '#6b7280' }}>Final: </span>
                        <span className={`score-value ${dispute.final_status}`}>
                          {dispute.final_status === 'success' ? 'Success' :
                           dispute.final_status === 'failed' ? 'Failed' : 'Bad Task'}
                        </span>
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#374151', marginTop: '6px' }}>
                      <strong>Note:</strong> {dispute.note.length > 100 ? dispute.note.substring(0, 100) + '...' : dispute.note}
                    </div>
                    {dispute.reason && dispute.status !== 'pending' && (
                      <div style={{ fontSize: '12px', color: '#374151', marginTop: '6px' }}>
                        <strong>Response:</strong> {dispute.reason.length > 100 ? dispute.reason.substring(0, 100) + '...' : dispute.reason}
                      </div>
                    )}
                    {isDisputeMode && onOpenDisputeModal && userPermissions.some(perm =>
                      perm.startsWith('resolve_disputes_') ||
                      (software && perm === `resolve_disputes_${software}`)
                    ) && (
                      <button
                        style={{
                          marginTop: '10px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDisputeModal(dispute);
                        }}
                      >
                        <span>💬</span>
                        {dispute.status !== 'pending' ? 'Edit Resolution' : 'Respond'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : disputeInfo && (
            <div className="sidebar-section dispute-info-section">
              <h3>Dispute Information</h3>
              <div className="dispute-info-content">
                <div className="dispute-info-item">
                  <span className="dispute-info-type">Actual Score:</span>
                  <span className={`dispute-status-value ${disputeInfo.final_score === 1 ? 'success' : disputeInfo.final_score === 0 ? 'failed' : ''}`}>
                    {disputeInfo.final_score === 1 ? '✓ Success' :
                     disputeInfo.final_score === 0 ? '✗ Failed' :
                     'Not scored'}
                  </span>
                </div>
                <div className="dispute-info-item">
                  <span className="dispute-info-type">Proposed Score:</span>
                  <span className={`dispute-proposed-value ${disputeInfo.proposedScore}`}>
                    {disputeInfo.proposedScore === 'success' ? '✓ Success' :
                     disputeInfo.proposedScore === 'failed' ? '✗ Failed' :
                     '⚠ Bad Task'}
                  </span>
                </div>
                <div className="dispute-info-note">
                  <span className="dispute-info-type">Dispute Note:</span>
                  <div className="dispute-note-content">{disputeInfo.note}</div>
                </div>
                {disputeInfo.reason && disputeInfo.status !== 'pending' && (
                  <div className="dispute-info-response">
                    <span className="dispute-info-type">Dispute Response ({disputeInfo.status === 'accepted' ? 'Accepted' : 'Rejected'}):</span>
                    <div className="dispute-response-content">{disputeInfo.reason}</div>
                  </div>
                )}
                <div className="dispute-info-date">
                  Submitted on {new Date(disputeInfo.createdAt + 'Z').toLocaleString()}
                </div>
              </div>
              {isDisputeMode && onOpenDisputeModal && userPermissions.some(perm =>
                perm.startsWith('resolve_disputes_') ||
                (software && perm === `resolve_disputes_${software}`)
              ) && (
                <button
                  className="respond-dispute-button"
                  onClick={() => {
                    // Create a dispute object from the disputeInfo
                    const dispute: ScoreDispute = {
                      id: (disputeInfo as any).id || '',
                      problem_run_id: runId,
                      user_id: (disputeInfo as any).user_id || '',
                      status: disputeInfo.status as 'pending' | 'accepted' | 'rejected',
                      proposed_score: disputeInfo.proposedScore as 'success' | 'failed' | 'bad_task',
                      note: disputeInfo.note,
                      reason: disputeInfo.reason,
                      created_at: disputeInfo.createdAt,
                      updated_at: disputeInfo.createdAt,
                      final_score: disputeInfo.final_score !== undefined ? disputeInfo.final_score : null,
                      current_score: disputeInfo.current_score !== undefined ? disputeInfo.current_score : null,
                      problem_name: problemName,
                    };
                    onOpenDisputeModal(dispute);
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>💬</span>
                  {disputeInfo.status !== 'pending' ? 'Edit Dispute' : 'Respond to Dispute'}
                </button>
              )}
            </div>
          )}
          
          <div className="sidebar-section">
            <h3>Current Action</h3>
            {isTransitioning ? (
              <div className="skeleton-action-box">
                <div className="skeleton-loader skeleton-text title"></div>
                <div className="skeleton-loader skeleton-text long"></div>
                <div className="skeleton-loader skeleton-text medium"></div>
              </div>
            ) : currentScreenshot ? (
              <>
                <div className="action-summary">
                  <div className="action-number">
                    Action {currentIndex + 1} of {totalActions || 0}
                  </div>
                  <div className="action-description">
                    <div>{currentScreenshot.description || 'No action'}</div>
                    {showImpossibleMarker && (
                      <div className="impossible-marker">Marked Impossible</div>
                    )}
                  </div>
                  {thoughtProcessText && (
                    <div className="thought-process">
                      <strong>AI Thought Process:</strong>{' '}
                      {thoughtProcessText}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-action">No action available</div>
            )}
          </div>
        </div>

        <div className="sidebar-navigation">
          {/* Only show evaluation section when not viewing mistakes or in dispute mode and not soft-deleted and not running/pending/failed/not-completed and no error/missing screenshots */}
          {!viewingMistake && !isDisputeMode && !userLabelDeleted && runStatus !== "running" && runStatus !== "pending" && runStatus !== "failed" && !problemRunNotCompleted && !error && screenshotData && screenshotData.screenshots.length > 0 && (
            <div className="sidebar-section evaluation-section">
              <h3>Evaluate Task</h3>
              <div className="evaluation-buttons">
                {isTransitioning ? (
                  <>
                    <div className="skeleton-loader" style={{ flex: 1, height: '40px', borderRadius: '6px' }}></div>
                    <div className="skeleton-loader" style={{ flex: 1, height: '40px', borderRadius: '6px' }}></div>
                    <div className="skeleton-loader" style={{ flex: 1, height: '40px', borderRadius: '6px' }}></div>
                  </>
                ) : (
                  <>
                    <button
                      className={`evaluation-btn success ${evaluation === LabelType.SUCCESS ? 'selected' : ''} ${disableOverdueEvaluations ? 'overdue-disabled' : ''} ${evaluationsDisabled ? 'readonly' : ''}`.trim()}
                      onClick={() => handleEvaluation(LabelType.SUCCESS)}
                      disabled={submittingLabel || disableOverdueEvaluations || evaluationsDisabled}
                      title={
                        evaluationsDisabled
                          ? 'Tutorial mode: evaluations are disabled.'
                          : disableOverdueEvaluations
                          ? 'This project is past the due date'
                          : ''
                      }
                    >
                      ✓ Success
                    </button>
                    <button
                      className={`evaluation-btn fail ${evaluation === LabelType.FAILED ? 'selected' : ''} ${isBadTask ? 'bad-task-active' : ''} ${disableOverdueEvaluations ? 'overdue-disabled' : ''} ${evaluationsDisabled ? 'readonly' : ''}`.trim()}
                      onClick={() => handleEvaluation(LabelType.FAILED)}
                      disabled={submittingLabel || disableOverdueEvaluations || evaluationsDisabled}
                      title={
                        evaluationsDisabled
                          ? 'Tutorial mode: evaluations are disabled.'
                          : disableOverdueEvaluations
                          ? 'This project is past the due date'
                          : isBadTask
                          ? 'Marked as a bad task. Update or remove this from the problem page.'
                          : ''
                      }
                    >
                      ✗ Fail
                    </button>
                  </>
                )}
              </div>
              {evaluationsDisabled && (
                <div className="evaluation-readonly-hint">
                  Tutorial walkthrough – evaluations are disabled in this view.
                </div>
              )}
              {!isOnboarding && (labelReason || isBadTask) && (
                <div className={`evaluation-reason-display ${isBadTask ? 'bad-task' : ''}`.trim()}>
                  <span className="reason-display-text">
                    <strong>{isBadTask ? 'Bad Task Reason:' : 'Reason:'}</strong>{' '}
                    {labelReason || (isBadTask ? 'Marked as Bad Task' : 'No reason provided.')}
                  </span>
                </div>
              )}
            </div>
          )}
          {/* Only show task navigation if navigation callbacks are provided */}
          {(onNextProblem || onPrevProblem) && (
            <div className="sidebar-section task-navigation">
              {isTransitioning ? (
                <>
                  <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px' }}></div>
                  <div className="skeleton-loader" style={{ width: '48%', height: '44px', borderRadius: '6px' }}></div>
                </>
              ) : (
                <>
                  <button
                    className="task-nav-btn prev-task"
                    onClick={() => {
                      setIsTransitioning(true);
                      setEvaluation(null);
                      setLabelReason('');
                      onPrevProblem && onPrevProblem();
                    }}
                    disabled={!hasPrevProblem}
                  >
                    ← Previous Run
                  </button>
                  <button
                    className="task-nav-btn next-task"
                    onClick={() => {
                      setIsTransitioning(true);
                      setEvaluation(null);
                      setLabelReason('');
                      onNextProblem && onNextProblem();
                    }}
                    disabled={!hasNextProblem}
                  >
                    Next Run →
                  </button>
                </>
              )}
            </div>
          )}
          <NavigationControls
            currentIndex={currentIndex}
            totalSteps={screenshotData?.screenshots?.length || 0}
            isPlaying={isPlaying}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onPlayPause={handlePlayPause}
            canGoPrevious={currentIndex > 0}
            canGoNext={currentIndex < (screenshotData?.screenshots?.length || 1) - 1}
            playbackSpeed={playbackSpeed}
            onSpeedChange={handleSpeedChange}
          />
          <KeyboardShortcuts />
        </div>
      </div>
      
      <div className="viewer-main" ref={mainContentRef}>
        <div 
          className="screenshot-wrapper" 
          onClick={handleScreenshotClick}
          onContextMenu={handleContextMenu}
        >
          {isTransitioning ? (
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: '#f8f8f8'
            }}>
              <div className="loading-spinner"></div>
            </div>
          ) : (loading || runId === "loading" || problemName === "Loading...") ? (
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: '#f8f8f8'
            }}>
              <div className="loading-spinner"></div>
            </div>
          ) : userLabelDeleted || runStatus === "pending" || runStatus === "running" || runStatus === "failed" || problemRunNotCompleted ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'normal',
              color: '#666',
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8f9fa',
              border: '2px solid #dee2e6',
              borderRadius: '8px'
            }}>
              This AI run is now outdated. Please skip to the next video.
            </div>
          ) : (error && !problemRunNotCompleted) || (!screenshotData || screenshotData.screenshots.length === 0) ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#666',
              textAlign: 'center',
              padding: '20px'
            }}>
              This video is failing to load. Please report this bug and move on to the next video.
            </div>
          ) : currentScreenshot ? (
            <ScreenshotDisplay 
              screenshot={currentScreenshot} 
              nextScreenshot={nextScreenshot}
              navigationIndicator={navigationIndicator}
              indicatorKey={indicatorKey}
              onIndicatorComplete={handleIndicatorComplete}
            />
          ) : null}
        </div>
        
        {totalActions > 0 && (
          <div className="tool-output-wrapper">
            {isTransitioning || loading || !currentScreenshot ? (
              <div className="tool-output skeleton">
                <div className="tool-output-body">
                  <div className="tool-output-section">
                    <span className="tool-output-label">Command</span>
                    <div className="skeleton-loader" style={{ width: '100%', height: '48px', borderRadius: '6px' }}></div>
                  </div>
                  <div className="tool-output-section">
                    <span className="tool-output-label">Result</span>
                    <div className="skeleton-loader" style={{ width: '100%', height: '72px', borderRadius: '6px' }}></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`tool-output${toolPanelIsError ? ' error' : ''}`}>
                <div className="tool-output-body">
                  <div className="tool-output-section">
                    <span className="tool-output-label">Command</span>
                    <pre>{toolPanelCommand || '\u00A0'}</pre>
                  </div>
                  <div className="tool-output-section">
                    <span className="tool-output-label">{toolPanelResultLabel}</span>
                    <pre>{toolPanelResult || '\u00A0'}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="timeline-container">
          <input
            type="range"
            className="timeline-scrubber video-timeline"
            min="0"
            max={screenshotData?.screenshots?.length ? screenshotData.screenshots.length - 1 : 0}
            value={currentIndex}
            onChange={handleTimelineChange}
            onBlur={() => {
              // Refocus the viewer when timeline loses focus
              if (viewerRef.current) {
                viewerRef.current.focus();
              }
            }}
            style={{
              background: screenshotData?.screenshots && screenshotData.screenshots.length > 1 
                ? `linear-gradient(to right, #667eea 0%, #667eea ${(currentIndex / (screenshotData.screenshots.length - 1)) * 100}%, #e2e8f0 ${(currentIndex / (screenshotData.screenshots.length - 1)) * 100}%, #e2e8f0 100%)`
                : '#e2e8f0'
            }}
          />
          <div className="timeline-info">
            <span className="timeline-current">{currentIndex + 1}</span>
            <span className="timeline-separator">/</span>
            <span className="timeline-total">{screenshotData?.screenshots?.length || 0}</span>
          </div>
          <button
            className="fullscreen-toggle"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {/* Onboarding overlay - only show when screenshot data is loaded and not transitioning */}
      {showViewerTutorialOverlay && (
        <OnboardingOverlayNew
          currentStepId={onboardingStep}
          onStepComplete={handleOnboardingStepComplete}
          onStepPrevious={handleOnboardingStepPrevious}
          onComplete={handleOnboardingComplete}
          onNextTask={onNextProblem}
          currentTaskIndex={currentOnboardingTask}
          software={software}
          seekToFrame={setCurrentIndex}
          evaluationReady={overlayEvaluationReady}
        />
      )}
      
      {/* Completion Dialog */}
      {showCompletionDialog && (
        <div className="completion-dialog-overlay" onClick={handleCloseCompletionDialog}>
          <div className="completion-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>🎉 Congratulations!</h2>
            <p>
              You have finished evaluating all tasks. Thank you for your hard work!
            </p>
            <p>
              We will review your submissions and be in touch soon.
            </p>
            <p>
              Feel free to review or update any of your evaluations by navigating through the tasks.
            </p>
            <button className="close-dialog-btn" onClick={handleCloseCompletionDialog}>
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Wrong Evaluation Dialog */}
      {showWrongEvaluationDialog && (
        <div className="onboarding-overlay">
          <div className="onboarding-backdrop" onClick={() => setShowWrongEvaluationDialog(false)} />
          <div 
            className="onboarding-tooltip" 
            style={{ 
              position: 'fixed',
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              maxWidth: '400px'
            }}
          >
            <h3>Not Quite Right</h3>
            <p>That's not quite right! Reread the description and think about the possible answers.</p>
            
            <div className="tooltip-footer">
              <span className="step-indicator">
                Step {currentOnboardingTask === 1 ? "20" : currentOnboardingTask === 2 ? "22" : "31"}/32
              </span>
              <button 
                onClick={() => setShowWrongEvaluationDialog(false)} 
                className="tooltip-btn primary"
              >
                TRY AGAIN
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Correct Evaluation Dialog */}
      {showCorrectEvaluationDialog && (
        <div className="onboarding-overlay">
          <div className="onboarding-backdrop" />
          <div 
            className="onboarding-tooltip" 
            style={{ 
              position: 'fixed',
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              maxWidth: '450px'
            }}
          >
            <h3>Exactly!</h3>
            <p>This is a bad task because it specifies to increase the height of the pad to 40mm, even though the pad starts at 100mm. Therefore the input is not consistent with the task description.</p>
            
            <div className="tooltip-footer">
              <span className="step-indicator">Step 31/32</span>
              <button 
                onClick={() => {
                  setShowCorrectEvaluationDialog(false);
                  handleOnboardingStepComplete('task-5-evaluate');
                }} 
                className="tooltip-btn primary"
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onDownloadModelOutput={handleDownloadModelOutput}
          onDownloadStartState={problemId ? handleDownloadStartState : undefined}
          onCompareVideos={handleCompareVideos}
          software={software}
          showVideoComparison={!(isDisputeMode && userPermissions.some(perm => perm.startsWith('resolve_disputes_')))}
        />
      )}

      {/* First-time download tip */}
      {showFirstTimeTip && (
        <div
          className="first-time-tip-overlay"
          onClick={handleDismissFirstTimeTip}
        >
          <div
            className="first-time-tip-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="first-time-tip-header">
              💡 Tip
            </div>
            <div className="first-time-tip-message">
              You can right-click the video to download the AI's output file and the input state.
            </div>
            <button
              className="first-time-tip-button"
              onClick={handleDismissFirstTimeTip}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Video comparison tip */}
      {showVideoComparisonTip && (
        <div
          className="first-time-tip-overlay"
          onClick={handleDismissVideoComparisonTip}
        >
          <div
            className="first-time-tip-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="first-time-tip-header">
              💡 Tip
            </div>
            <div className="first-time-tip-message">
              You can compare the final output side-by-side with your solution by right-clicking the video and selecting "Compare Side-by-Side"!
            </div>
            <button
              className="first-time-tip-button"
              onClick={handleDismissVideoComparisonTip}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {downloadProgress !== null && (
        <DownloadToast
          filename={downloadFilename || 'Downloading...'}
          progress={downloadProgress}
          onClose={handleCloseDownloadToast}
        />
      )}

      <VideoComparisonModal
        isOpen={showVideoComparisonModal}
        onClose={() => setShowVideoComparisonModal(false)}
        problemRunId={runId}
        problemId={problemId}
        projectId={effectiveProjectId || null}
        isTutorial={isOnboarding}
      />

      {/* Playground Modal for Mattermost problems */}
      {problemId && software && (
        <PlaygroundModal
          isOpen={isPlaygroundModalOpen}
          onClose={() => setIsPlaygroundModalOpen(false)}
          problemId={problemId}
          software={software}
          problemPrompt={loadedPrompt || problemPrompt}
        />
      )}

      {/* Dispute Modal */}
      {showDisputeModal && mistakeInfo && (
        <DisputeModal
          isOpen={showDisputeModal}
          onClose={() => setShowDisputeModal(false)}
          onSubmit={handleDisputeSubmit}
          currentScore={mistakeInfo.actualResult as 'success' | 'failed'}
          problemName={problemName}
        />
      )}

      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Confirm Delete
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete this {deleteType === 'dispute' ? 'dispute' : 'mistake acknowledgment'}?
            </p>
            <p style={{ color: '#dc2626', fontWeight: 500, margin: '0 0 20px 0', fontSize: '14px' }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="delete-modal-overlay" onClick={() => setShowReasonModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Explain the reason for your decision</h3>
            <p style={{ marginBottom: '1rem', color: '#4b5563', fontSize: '0.875rem' }}>
              Please provide a brief explanation for your evaluation.
            </p>
            <textarea
              className="reason-modal-textarea"
              value={modalReasonInput}
              onChange={(e) => setModalReasonInput(e.target.value)}
              placeholder="Enter your reason here..."
              rows={4}
              autoFocus
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.875rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            <div className="delete-modal-buttons">
              <button
                className="delete-modal-btn cancel"
                onClick={() => {
                  setShowReasonModal(false);
                  setPendingEvaluation(null);
                  setModalReasonInput('');
                }}
              >
                Cancel
              </button>
              <button
                className="delete-modal-btn delete"
                onClick={handleReasonSubmit}
                disabled={!modalReasonInput.trim()}
                style={{
                  background: !modalReasonInput.trim() ? '#ccc' : '#667eea',
                  cursor: !modalReasonInput.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ScreenshotViewer;
